import { getAuthorized } from "@/lib/fetch";
import { postAuthorized } from "@/lib/fetch";
import { patchAuthorized } from "@/lib/fetch";
import type {
  PortfolioComment,
  CreatePortfolioCommentPayload,
} from "../portfolio/domain/types";
import type {
  ValidationEvent,
  CreateValidationEventPayload,
} from "../validation/domain/types";

const backendBaseUrl =
  import.meta.env.VITE_BACKEND_URL || "http://localhost:8000";
export const R1_BASE_URL = `${backendBaseUrl.replace(/\/+$/, "")}/r1`;

export function buildR1DownloadUrl(
  tableName: string,
  params: URLSearchParams,
): string {
  const query = params.toString();
  const path = `${R1_BASE_URL}/${tableName}/download-excel`;
  return query ? `${path}?${query}` : path;
}

async function getJson<T>(
  path: string,
  params?: Record<string, string> | URLSearchParams,
): Promise<T> {
  const queryString = params ? new URLSearchParams(params).toString() : "";
  const query = queryString ? `?${queryString}` : "";
  const response = await getAuthorized(`${R1_BASE_URL}${path}${query}`);
  return response.json();
}

export interface R1Fund {
  id: number;
  fund_id: number; // alias — backend may return either
  fund_name: string;
  fund_currency?: string;
  manager?: string;
  fund_domicile?: string;
  vintage_year?: number;
  strategy_focus?: string;
  geographic_focus?: string;
  asset_type_fund?: string;
  latest_reporting_period?: string;
  // PE Funds table extra fields
  name_of_general_partner?: string;
  legal_form_fund?: string;
  fund_investment_state?: string;
  team_location_country?: string;
  fund_with_connection_to_germany?: boolean;
  commitment_date?: string;
  signature_date?: string;
  liquidation_date?: string;
  first_closing_date?: string;
  final_closing_date?: string;
  first_investment_date_fund?: string;
  end_of_investment_period?: string;
  planned_end_date?: string;
  generation?: string;
  sector_focus?: string;
  product_type?: string;
  lei_code_fund?: string;
  in_which_closing_joined?: string;
  pct_of_private_investors?: number;
  gp_name?: string;
  management_company_id?: number;
  trr_required?: boolean;
  deadline_nb_days?: number;
  first_production?: boolean;
}

export interface R1FundGeography {
  fund_id: number;
  fund_name: string;
  // ISO 3166-1 numeric code (e.g. "250" for France, "831" for Guernsey),
  // or null when the backend couldn't resolve the stored domicile value.
  country_iso_numeric: string | null;
}

export interface R1PositionGeography {
  fund_id: number;
  issuer_id: number;
  company_name: string;
  // Same ISO 3166-1 numeric convention as R1FundGeography, resolved
  // server-side from the issuer's domicile.
  country_iso_numeric: string | null;
}

// CONTRACT: getPositionsGeography() returns one row per POSITION, not per
// unique company — a company held by 3 funds at the same quarter yields 3
// rows with the same issuer_id/company_name. This is intentional (R1 Level's
// "Portfolio Positions Map" is meant to count fund exposures, not unique
// portfolio companies). Contrast with AnalyticsView's "Portfolio Companies
// Map" (Fund Level), which dedupes by issuer_id since it's scoped to a
// single fund and represents that fund's unique portfolio companies.

export interface R1FundBreakdownEntry {
  label: string;
  count: number;
  // 0-100, pre-computed server-side against the known (non-null) total.
  percentage: number;
}

export interface R1FundBreakdownDimension {
  // 0-100 — share of funds that have a non-null value for this field.
  fill_rate: number;
  breakdown: R1FundBreakdownEntry[];
}

export interface R1FundBreakdowns {
  strategy: R1FundBreakdownDimension;
  sector: R1FundBreakdownDimension;
  vintage: R1FundBreakdownDimension;
  country: R1FundBreakdownDimension;
  currency: R1FundBreakdownDimension;
}

export interface ReconciliationCompanyRow {
  issuer_id: number;
  company_name: string;
  status: "removed" | "added" | "active";
  first_investment_date: string | null;
  positions: Record<string, unknown>[];
  previous_positions: Record<string, unknown>[];
}

export interface ReconciliationCompaniesResponse {
  count: number;
  added_count: number;
  removed_count: number;
  active_count: number;
  total_positions_count: number;
  pdf_raw_count: number;
  trr_raw_count: number;
  data: ReconciliationCompanyRow[];
}

export interface R1PagedResponse<T> {
  data: T[];
  total?: number;
  count?: number;
}

export interface R1CountedResponse<T> {
  data: T[];
  count: number;
  summary?: {
    unresolved: number;
    trr_vs_golden: number;
    pdf_vs_golden: number;
    proposed: number;
  };
}

export interface R1ReconciliationAuditItem {
  id: number;
  created_at: string;
  entity_type: "issuer" | "position" | "fund";
  entity_id: number;
  event_label?: string;
  field_key: string;
  field_label?: string;
  previous_value: string;
  next_value: string;
  trr_value?: string | null;
  pdf_value?: string | null;
  golden_value?: string | null;
  comment: string;
  user_email: string | null;
  company_name: string | null;
}

export interface R1ReconciliationControlItem {
  id: number;
  entity_type?: "issuer" | "position" | "fund" | null;
  fund_id: number | null;
  fund: string | null;
  issuer_id: number | null;
  issuer: string | null;
  section_name?: string | null;
  position_id: number | null;
  field_name: string | null;
  level?: "fund" | "company_numeric" | null;
  field_group?: "fund" | "company" | "numeric" | "other" | null;
  cell_key: string | null;
  reconciliation_status: string | null;
  business_key: string | null;
  reporting_date: string | null;
  trr_value?: string | null;
  pdf_value?: string | null;
  golden_value?: string | null;
  materiality_abs?: number | string | null;
  delta_raw_golden?: number | string | null;
  reason_code?: string | null;
  last_seen_at?: string | null;
  field_key_canonical?: string | null;
  trr_raw_value?: string | null;
  pdf_raw_value?: string | null;
  raw_match_status?:
    | "matched"
    | "missing_trr"
    | "missing_pdf"
    | "missing_both"
    | "ambiguous"
    | null;
  cell_status?: "valid" | "conflict" | "missing" | "manual" | null;
  previous_value?: string | null;
  delta_label?: string | null;
  delta_tone?: "success" | "danger" | "warning" | "default" | null;
  display_value?: string | null;
}

export interface R1ReconciliationOverridePayload {
  entity_type: "issuer" | "position" | "fund";
  entity_id: number;
  field_key: string;
  value: string;
  comment?: string;
  reconciliation_control_id?: number;
  fund_id?: number;
  reporting_date?: number;
}

export interface R1ReconciliationOverrideResponse {
  success: boolean;
  id: number;
  updated: boolean;
  warning?: {
    validated_funds: Array<{ fund_id: number; fund_name: string }>;
    non_validated_funds: Array<{ fund_id: number; fund_name: string }>;
  };
}

export interface R1ReconciliationFalsePositivePayload {
  reconciliation_control_id?: number;
  comment: string;
  fund_id?: number;
  issuer_id?: number;
  position_id?: number;
  section_name?: string;
  field_name?: string;
  reporting_date?: number;
  trr_value?: string;
  pdf_value?: string;
  golden_value?: string;
  business_key?: string;
}

export interface R1EmailLogFundUpdatePayload {
  fund_id: number | null;
}

export interface R1EmailLogUpdateResponse {
  id: number;
  fund_id: number | null;
  fund_name: string | null;
}

export interface R1PositionsQueryParams {
  period?: string | null;
  selection?: R1FundSelection;
  includePreviousPeriod?: boolean;
  page?: number;
  pageSize?: number;
  companyNameSearch?: string;
  fundNameSearch?: string;
  fundGpIdSearch?: string;
  gpIdPcSearch?: string;
  issuerId?: number;
}

export type R1FundSelection = { mode: "all" } | { mode: "ids"; ids: number[] };

export function buildFundScopeParams(
  selection: R1FundSelection,
): URLSearchParams {
  const params = new URLSearchParams();
  if (selection.mode === "all") {
    params.set("fund_scope", "all");
    return params;
  }
  params.set("fund_scope", "ids");
  params.set("fund_ids_csv", selection.ids.join(","));
  return params;
}

export function fundSelectionKey(selection: R1FundSelection): string {
  if (selection.mode === "all") return "all";
  return [...selection.ids].sort((a, b) => a - b).join(",");
}

export const r1Api = {
  getFunds(params?: { slim?: boolean; ids?: number[]; selection?: R1FundSelection }): Promise<R1PagedResponse<R1Fund>> {
    const q: Record<string, string> = {};
    if (params?.slim) q["slim"] = "true";
    if (params?.selection) {
      if (params.selection.mode === "ids" && params.selection.ids.length > 0) {
        q["ids_csv"] = params.selection.ids.join(",");
      }
      // mode "all" → no filter, backend returns everything
    } else if (params?.ids?.length) {
      q["ids_csv"] = params.ids.join(",");
    }
    return getJson("/r1_pe_funds", q);
  },

  // Slim, pre-normalized rows for the Funds Map: one entry per fund with
  // its domicile resolved to an ISO 3166-1 numeric code server-side, so the
  // frontend can match it directly against topojson `geo.id` without any
  // country-name parsing.
  getFundsGeography(): Promise<R1FundGeography[]> {
    return getJson("/r1_pe_funds/geography");
  },

  // Slim, pre-normalized rows for the Portfolio Companies Map: one entry
  // per position across ALL funds at the given quarter, with the issuer's
  // country pre-joined and pre-resolved to an ISO 3166-1 numeric code.
  getPositionsGeography(period: string): Promise<R1PositionGeography[]> {
    return getJson("/r1_positions/geography", { period });
  },

  // Pre-aggregated counts + percentages for the R1 Level breakdown pies —
  // avoids shipping every fund's full record just to group by 4 columns.
  getFundsBreakdown(): Promise<R1FundBreakdowns> {
    return getJson("/r1_pe_funds/breakdown");
  },

  getReportingPeriods(fundIds: number[]): Promise<string[]> {
    if (fundIds.length === 0) return Promise.resolve([]);
    return getJson("/r1_documents/reporting-periods", {
      fund_ids: fundIds.join(","),
    });
  },

  getReportingPeriodsPerFund(
    selection: R1FundSelection,
  ): Promise<Record<string, string[]>> {
    const params = buildFundScopeParams(selection);
    return getJson("/r1_documents/reporting-periods-per-fund", params);
  },

  getIssuers(params?: {
    page?: number;
    pageSize?: number;
    name?: string;
    gpId?: string;
    regNumber?: string;
    country?: string;
    ids?: number[];
    slim?: boolean;
  }): Promise<R1PagedResponse<Record<string, unknown>>> {
    const q: Record<string, string> = {};
    if (params?.page) q["page"] = String(params.page);
    if (params?.pageSize) q["page_size"] = String(params.pageSize);
    if (params?.name) q["name_search"] = params.name;
    if (params?.gpId) q["gp_id_contains"] = params.gpId;
    if (params?.regNumber) q["reg_number_contains"] = params.regNumber;
    if (params?.country) q["country_contains"] = params.country;
    if (params?.ids?.length) q["ids_csv"] = params.ids.join(",");
    if (params?.slim) q["slim"] = "true";
    return getJson("/r1_issuers", q);
  },

  getIssuersView(params?: {
    page?: number;
    pageSize?: number;
    name?: string;
    gpId?: string;
    regNumber?: string;
    country?: string;
    idEquals?: number;
    gpIdEmpty?: boolean;
    multiFund?: boolean;
    noFund?: boolean;
    sortBy?: "id" | "name";
    sortDir?: "asc" | "desc";
  }): Promise<R1PagedResponse<Record<string, unknown>>> {
    const q: Record<string, string> = {};
    if (params?.page) q["page"] = String(params.page);
    if (params?.pageSize) q["page_size"] = String(params.pageSize);
    if (params?.name) q["name_search"] = params.name;
    if (params?.gpId) q["gp_id_contains"] = params.gpId;
    if (params?.regNumber) q["reg_number_contains"] = params.regNumber;
    if (params?.country) q["country_contains"] = params.country;
    if (params?.idEquals) q["id_equals"] = String(params.idEquals);
    if (params?.gpIdEmpty) q["gp_id_empty"] = "true";
    if (params?.multiFund) q["multi_fund"] = "true";
    if (params?.noFund) q["no_fund"] = "true";
    if (params?.sortBy) q["sort_by"] = params.sortBy;
    if (params?.sortDir) q["sort_dir"] = params.sortDir;
    return getJson("/r1_issuers_view", q);
  },

  getPositions(
    paramsOrPeriod?: R1PositionsQueryParams | string | null,
  ): Promise<R1PagedResponse<Record<string, unknown>>> {
    const params = new URLSearchParams();
    const period =
      typeof paramsOrPeriod === "string" || paramsOrPeriod === null
        ? paramsOrPeriod
        : paramsOrPeriod?.period;
    const selection =
      typeof paramsOrPeriod === "object" && paramsOrPeriod !== null
        ? (paramsOrPeriod.selection ?? { mode: "all" as const })
        : { mode: "all" as const };
    const includePreviousPeriod =
      typeof paramsOrPeriod === "object" && paramsOrPeriod !== null
        ? Boolean(paramsOrPeriod.includePreviousPeriod)
        : false;
    const includePreviousPeriodAllowed =
      includePreviousPeriod && selection.mode === "ids";
    const page =
      typeof paramsOrPeriod === "object" && paramsOrPeriod !== null
        ? paramsOrPeriod.page
        : undefined;
    const pageSize =
      typeof paramsOrPeriod === "object" && paramsOrPeriod !== null
        ? paramsOrPeriod.pageSize
        : undefined;

    buildFundScopeParams(selection).forEach((value, key) =>
      params.set(key, value),
    );
    if (period) params.set("reporting_date_equals", period);
    if (includePreviousPeriodAllowed)
      params.set("include_previous_period", "true");
    if (page) params.set("page", String(page));
    if (pageSize) params.set("page_size", String(pageSize));
    const companyNameSearch =
      typeof paramsOrPeriod === "object" && paramsOrPeriod !== null
        ? paramsOrPeriod.companyNameSearch
        : undefined;
    const fundNameSearch =
      typeof paramsOrPeriod === "object" && paramsOrPeriod !== null
        ? paramsOrPeriod.fundNameSearch
        : undefined;
    const fundGpIdSearch =
      typeof paramsOrPeriod === "object" && paramsOrPeriod !== null
        ? paramsOrPeriod.fundGpIdSearch
        : undefined;
    const gpIdPcSearch =
      typeof paramsOrPeriod === "object" && paramsOrPeriod !== null
        ? paramsOrPeriod.gpIdPcSearch
        : undefined;
    const searchIssuerId =
      typeof paramsOrPeriod === "object" && paramsOrPeriod !== null
        ? paramsOrPeriod.issuerId
        : undefined;
    if (companyNameSearch) params.set("company_name_search", companyNameSearch);
    if (fundNameSearch) params.set("fund_name_search", fundNameSearch);
    if (fundGpIdSearch) params.set("fund_gp_id_search", fundGpIdSearch);
    if (gpIdPcSearch) params.set("gp_id_pc_search", gpIdPcSearch);
    if (searchIssuerId != null) params.set("issuer_id", String(searchIssuerId));
    return getJson("/r1_positions", params);
  },

  getPositionsIssuers(
    fundId: number,
  ): Promise<R1PagedResponse<Record<string, unknown>>> {
    return getJson("/r1_positions_issuers", {
      fund_id_equals: String(fundId),
    });
  },

  getPositionsIssuersReconciliationAudit(
    fundId?: number | null,
  ): Promise<R1CountedResponse<R1ReconciliationAuditItem>> {
    const params: Record<string, string> = {};
    if (fundId) params.fund_id_equals = String(fundId);
    return getJson(
      "/r1_positions_issuers/reconciliation_audit",
      Object.keys(params).length > 0 ? params : undefined,
    );
  },

  getPositionsIssuersReconciliationControls(
    selection: R1FundSelection,
    period?: string | null,
    issuerIdsCsv?: string,
  ): Promise<R1CountedResponse<R1ReconciliationControlItem>> {
    const params = buildFundScopeParams(selection);
    if (period) params.set("reporting_date_equals", period);
    if (issuerIdsCsv) params.set("issuer_ids_csv", issuerIdsCsv);
    return getJson(
      "/r1_positions_issuers/reconciliation_controls",
      params.toString() ? params : undefined,
    );
  },

  getPositionsIssuersReconciliationCells(
    selection: R1FundSelection,
    period?: string | null,
    issuerIdsCsv?: string,
  ): Promise<R1CountedResponse<R1ReconciliationControlItem>> {
    const params = buildFundScopeParams(selection);
    if (period) params.set("reporting_date_equals", period);
    if (issuerIdsCsv) params.set("issuer_ids_csv", issuerIdsCsv);
    return getJson(
      "/r1_positions_issuers/reconciliation_cells",
      params.toString() ? params : undefined,
    );
  },

  getReconciliationCompanies(
    fundId: number,
    period: string,
    page: number,
    pageSize: number,
  ): Promise<ReconciliationCompaniesResponse> {
    return getJson("/reconciliation/companies", {
      fund_id: String(fundId),
      period,
      page: String(page),
      page_size: String(pageSize),
    });
  },

  savePositionsIssuersReconciliationOverride(
    payload: R1ReconciliationOverridePayload,
  ): Promise<R1ReconciliationOverrideResponse> {
    return postAuthorized(
      `${R1_BASE_URL}/r1_positions_issuers/reconciliation_override`,
      payload,
    );
  },

  createPositionsIssuersReconciliationFalsePositive(
    payload: R1ReconciliationFalsePositivePayload,
  ): Promise<{ success: boolean; reconciliation_control_id: number }> {
    return postAuthorized(
      `${R1_BASE_URL}/r1_positions_issuers/reconciliation_false_positive`,
      payload,
    );
  },

  getCapitalAccount(
    fundId: number,
    period?: string | null,
    slim = false,
  ): Promise<R1PagedResponse<Record<string, unknown>>> {
    const params = new URLSearchParams();
    params.set("fund_scope", "all");
    params.set("fund_id_equals", String(fundId));
    if (period) params.set("reporting_date_equals", period);
    if (slim) params.set("slim", "true");
    return getJson("/r1_capital_account", params);
  },

  getManagementCompanies(
    selection: R1FundSelection,
  ): Promise<R1PagedResponse<Record<string, unknown>>> {
    const params = buildFundScopeParams(selection);
    return getJson("/r1_management_companies", params);
  },

  getFundsPortfolio(
    selection: R1FundSelection,
    period?: string | null,
  ): Promise<R1PagedResponse<Record<string, unknown>>> {
    const params = buildFundScopeParams(selection);
    if (period) params.set("quarter_equals", period);
    return getJson("/r1_funds_portfolio", params);
  },

  getFundsPortfolioRow(
    fundId: number,
    quarter: string,
  ): Promise<R1PagedResponse<Record<string, unknown>>> {
    return getJson("/r1_funds_portfolio", {
      fund_scope: "ids",
      fund_ids_csv: String(fundId),
      quarter_equals: quarter,
    });
  },

  getFundsPortfolioQuarters(selection: R1FundSelection): Promise<string[]> {
    const params = buildFundScopeParams(selection);
    return getJson("/r1_funds_portfolio/quarters", params);
  },

  getPortfolioComments(
    fundId: number,
    quarter: string,
  ): Promise<PortfolioComment[]> {
    return getJson("/r1_portfolio_comments", {
      fund_id: String(fundId),
      quarter_equals: quarter,
    });
  },

  createPortfolioComment(
    payload: CreatePortfolioCommentPayload,
  ): Promise<PortfolioComment> {
    return postAuthorized(`${R1_BASE_URL}/r1_portfolio_comments`, payload);
  },

  getValidationCurrentState(
    fundId: number,
    quarter: string,
  ): Promise<ValidationEvent[]> {
    return getJson("/r1_validation_events/current", {
      fund_id: String(fundId),
      quarter_equals: quarter,
    });
  },

  createValidationEvent(
    payload: CreateValidationEventPayload,
  ): Promise<ValidationEvent> {
    return postAuthorized(`${R1_BASE_URL}/r1_validation_events`, payload);
  },

  getEmailLogs(
    selection: R1FundSelection,
    period?: string | null,
    quarterIsNull = false,
  ): Promise<R1PagedResponse<Record<string, unknown>>> {
    const params = buildFundScopeParams(selection);
    if (quarterIsNull) {
      params.set("quarter_is_null", "true");
    } else if (period) {
      params.set("quarter_equals", period);
    }
    return getJson("/r1_email_logs", params);
  },

  getEmailLogQuarters(selection: R1FundSelection): Promise<string[]> {
    const params = buildFundScopeParams(selection);
    return getJson("/r1_email_logs/quarters", params);
  },

  updateEmailLogFund(
    emailLogId: number,
    payload: R1EmailLogFundUpdatePayload,
  ): Promise<R1EmailLogUpdateResponse> {
    return patchAuthorized(
      `${R1_BASE_URL}/r1_email_logs/${emailLogId}`,
      payload,
    );
  },

  getDocumentsForFundQuarter(
    fundId: number,
    quarter: string,
  ): Promise<
    {
      id: number;
      fund_id: number;
      file_name: string;
      file_type: string | null;
      reporting_date: number;
      processing_status: string | null;
      created_at: string | null;
    }[]
  > {
    return getJson("/raw_data/documents", {
      fund_id: String(fundId),
      reporting_date_equals: quarter,
    });
  },

  getRawPortfolioPositions(
    docId: number,
  ): Promise<{ count: number; data: Record<string, unknown>[] }> {
    return getJson("/raw_portfolio_positions", {
      r1_document_id: String(docId),
    });
  },

  getRawCapitalAccount(
    docId: number,
  ): Promise<{ count: number; data: Record<string, unknown>[] }> {
    return getJson("/raw_capital_account", {
      r1_document_id: String(docId),
    });
  },

  getRawGeneralInformation(
    docId: number,
  ): Promise<{ count: number; data: Record<string, unknown>[] }> {
    return getJson("/raw_general_information", {
      r1_document_id: String(docId),
    });
  },

  getRawFundOverview(
    docId: number,
  ): Promise<{ count: number; data: Record<string, unknown>[] }> {
    return getJson("/raw_fund_overview", {
      r1_document_id: String(docId),
    });
  },

  getRawCapitalAccountByFund(
    selection: R1FundSelection,
    period: string,
  ): Promise<{ count: number; data: Record<string, unknown>[] }> {
    const params = buildFundScopeParams(selection);
    params.set("reporting_date_equals", period);
    return getJson("/raw_capital_account/by_fund", params);
  },

  getCapitalAccountBatch(
    selection: R1FundSelection,
    period?: string | null,
  ): Promise<R1PagedResponse<Record<string, unknown>>> {
    const params = buildFundScopeParams(selection);

    if (period) {
      params.set("reporting_date_equals", period);
    } else {
      params.set("most_recent", "true");
    }

    return getJson("/r1_capital_account", params);
  },
};
